import { Component, DestroyRef, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';

import { projectStatuses } from '../../../../components/project/common/project.constants';
import { CHART_COLOURS, Constants, getStatuses, PROJECT_STATUS } from '../../../../helpers';
import { AcceptanceEmail, ContactClient, Project } from '../../../../interfaces';
import { ProjectModel } from '../../../../models';
import { ClientService } from '../../../../services/client.service';
import { textConstants } from './project-card.constants';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
	selector: 'app-project-card',
	templateUrl: './project-card.component.html',
	styleUrls: ['./project-card.component.scss'],
})
export class ProjectCardComponent implements OnInit, OnDestroy {
	@ViewChild('progressBar', { static: true })
	@ViewChild('comments') public comments: ElementRef;
	private progressBar: ElementRef;

	@Input() public project: ProjectModel;
	@Input() public targetProject: boolean = false;
	@Input() set selectedClient(val) {
		this._selectedClient = val;
		this.updateSelectedClientSuccessRate();
	}
	get selectedClient() {
		return this._selectedClient;
	}

	@Output() public selectedClientChange = new EventEmitter<string>();
	@Output() public selectedAcceptanceEmail = new EventEmitter<AcceptanceEmail>();
	@Output() public updateProjectStatus = new EventEmitter<ProjectModel>();

	public chartGreen: CHART_COLOURS = CHART_COLOURS.green;
	public chartLightGreen: CHART_COLOURS = CHART_COLOURS.revenue;
	public chartRed: CHART_COLOURS = CHART_COLOURS.red;
	public chartYellow: CHART_COLOURS = CHART_COLOURS.profit;
	public clientSuccessRates: any = {};
	public grossProfitChartColour: CHART_COLOURS;
	public incompleteColour: CHART_COLOURS = CHART_COLOURS.grey;
	public progressBarPercentage: number = 69;
	public PROJECT_INDUSTRIES = Constants.PROJECT_INDUSTRIES;
	public PROJECT_SUB_INDUSTRIES = Constants.PROJECT_SUB_INDUSTRIES;
	public projectStatuses: typeof projectStatuses = projectStatuses;
	public grossProfitLabels: string[] = ['Profit', 'Cost'];
	public selectedClientSuccessRate = { valueComplete: 0, valueIncomplete: 0 };
	public selectedClientGrossProfit = { valueComplete: 0, valueIncomplete: 0 };
	public strikeRateChartColour: CHART_COLOURS;
	public successRateLabels: string[] = ['Won', 'Quoted'];
	public textConstants: typeof textConstants = textConstants;
	public originalStatus: string;

	private destroyRef = inject(DestroyRef);
	private _selectedClient: ContactClient;

	constructor(private clientService: ClientService) {}

	public ngOnInit() {
		//this.progressBar.nativeElement.style.width = this.progressBarPercentage.toString() + '%';
		this.originalStatus = this.project.status;
		this.selectedClient = this.project.clients[0];

		this.clientService.clientSuccessRatesObservable.pipe(
			takeUntilDestroyed(this.destroyRef))
			.subscribe(updatedSuccessRates => {
				this.clientSuccessRates = updatedSuccessRates;
				this.updateSelectedClientSuccessRate();
			});
	}

	public ngOnDestroy(): void {
		this.selectedClientChange.complete();
		this.selectedAcceptanceEmail.complete();
	}

	public consumeClick(event: MouseEvent): void {
		event.stopPropagation();
		event.preventDefault();
	}

	public statusPillSelect(item: ProjectModel): void {
		this.updateProjectStatus.emit(item);
	}

	public customSearch(term: string, item: any) {
		term = term.toLocaleLowerCase();
		return (
			item.firstName.toLocaleLowerCase().indexOf(term) > -1 ||
			item.lastName.toLocaleLowerCase().indexOf(term) > -1 ||
			(item.firstName + ' - ' + item.lastName).toLocaleLowerCase().indexOf(term) > -1
		);
	}

	public showEmailDialog(isAcceptance: boolean, project: Project): void {
		this.selectedAcceptanceEmail.emit({
			isAcceptance,
			project: new ProjectModel(project),
		});
	}

	public scrollToBottom(): void {
		setTimeout(() => (this.comments.nativeElement.scrollTop = this.comments.nativeElement.scrollHeight), 10);
	}

	private updateSelectedClientSuccessRate() {
		if (!this.selectedClient) {
			return;
		}

		const id = this.selectedClient.company?._id ?? this.selectedClient.company?.id;
		const rate = this.clientSuccessRates[id];

		if (rate) {
			const strikeRate = rate.totalWonCount && rate.totalProjectCount
				? rate.totalWonCount / rate.totalProjectCount
				: 0;

			const grossProfit = rate.totalRevenue > 0
				? (rate.totalRevenue - rate.totalCost) / rate.totalRevenue
				: 0;

			this.selectedClientSuccessRate = {
				valueComplete: strikeRate,
				valueIncomplete: 1 - strikeRate,
			};

			this.selectedClientGrossProfit = {
				valueComplete: grossProfit,
				valueIncomplete: 1 - grossProfit,
			}

			this.strikeRateChartColour = this.getChartColourFor(strikeRate);
			this.grossProfitChartColour = this.getChartColourFor(grossProfit);
		}
	}

	private getChartColourFor(value: number) {
		switch (true) {
			case value >= 0.5:
				return this.chartGreen;
			case value >= 0.25:
				return this.chartLightGreen;
			case value >= 0.15:
				return this.chartYellow;
			default:
				return this.chartRed;
		}
	}

	protected readonly PROJECT_STATUS = PROJECT_STATUS;
	protected readonly Object = Object;
	protected readonly getStatuses = getStatuses;
}
